<template>
  <div class="announcement-form-container">
    <div v-if="isLoading" class="loading-spinner">
      <p><i class="fas fa-spinner fa-spin"></i> 載入公告資料...</p>
    </div>

    <form v-else @submit.prevent="submitForm" class="edit-form">
      <div class="form-group">
        <label>封面圖片</label>
        <div class="image-upload-wrapper">
          <div class="image-preview">
            <img v-if="announcement.coverImageUrl" :src="announcement.coverImageUrl" alt="Cover Preview">
            <div v-else class="image-placeholder"><i class="fas fa-image"></i></div>
          </div>
          <div class="upload-actions">
            <input type="file" ref="fileInputRef" @change="handleCoverImageChange"
                   accept="image/jpeg, image/png, image/gif, image/webp" style="display: none;">
            <button type="button" style="background-color: rgba(80, 80, 90, 0.7);color: #c0c0c0;" class="action-btn" @click="triggerFileInput" :disabled="isUploading">
              <i v-if="!isUploading" class="fas fa-upload"></i>
              <i v-else class="fas fa-spinner fa-spin"></i>
              {{ isUploading ? '上傳中...' : '選擇封面' }}
            </button>
            <small>建議尺寸 800x450。僅上傳，不會立即儲存。</small>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label for="announcementTitle">標題</label>
        <input type="text" id="announcementTitle" v-model="announcement.title" required class="form-input">
      </div>

      <div class="form-group">
        <label for="announcementType">公告類型</label>
        <select id="announcementType" v-model="announcement.type" required class="form-input">
          <option value="一般公告">一般公告</option>
          <option value="系統維護">系統維護</option>
          <option value="活動快訊">活動快訊</option>
          <option value="重要公告">重要公告</option>
        </select>
      </div>

      <div class="form-group editor-form-group">
        <label>完整內容</label>
        <Editor
            v-if="showEditor"

            v-model="announcement.content"
            :init="tinymceInitOptions"
            class="tinymce-editor-instance"
        />
        <div v-else class="editor-loading">
          <p><i class="fas fa-spinner fa-spin"></i> 編輯器載入中...</p>
        </div>
      </div>

      <div class="form-group-inline">
        <div class="form-group">
          <label for="announcementSort">排序</label>
          <input type="number" id="announcementSort" v-model.number="announcement.sort" required class="form-input">
        </div>
        <div class="form-group toggle-form-group">
          <label for="announcementEnabled" class="toggle-switch-label">發布狀態：</label>
          <label class="switch">
            <input type="checkbox" id="announcementEnabled" v-model="announcement.enabled">
            <span class="slider round"></span>
          </label>
        </div>
      </div>

      <div class="form-actions">
        <button type="button" @click="$emit('close')" class="action-btn cancel-btn">返回列表</button>
        <button type="submit" class="action-btn save-btn" :disabled="isSaving || isLoading">
          <span v-if="isSaving"><i class="fas fa-spinner fa-spin"></i> 儲存中...</span>
          <span v-else>儲存公告</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
// eslint-disable-next-line no-unused-vars
import {computed, onMounted, ref, watch, nextTick} from 'vue';
import {useToast} from 'vue-toastification';
import {createAnnouncement, getAnnouncementById, updateAnnouncement} from '@/api/announcement.api.js';
import {uploadAnnouncementImage, uploadAnnouncementRichtextImage} from '@/api/file.api.js';
import Editor from '@/components/LocalRichTextEditor.vue';

// eslint-disable-next-line no-undef
const props = defineProps({
  announcementId: {type: Number, default: null}
});
// eslint-disable-next-line no-undef
const emit = defineEmits(['save-success', 'cancel']);

const initialAnnouncement = {title: '', content: '', enabled: true, sort: 100, coverImageUrl: null, type: '一般公告'};
const announcement = ref({...initialAnnouncement});
const isLoading = ref(false);
const isSaving = ref(false);
const isUploading = ref(false);
const fileInputRef = ref(null);
const showEditor = ref(false);
const isEditMode = computed(() => props.announcementId != null);
const toast = useToast();

const tinymceInitOptions = ref({
  language: 'zh_TW',
  height: 500,
  plugins: 'preview importcss searchreplace autolink autosave save directionality code visualblocks visualchars fullscreen image link media table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help charmap quickbars emoticons accordion',
  menubar: 'file edit view insert format tools table help',
  toolbar: 'undo redo | accordion accordionremove | blocks fontfamily fontsize | bold italic underline strikethrough | align numlist bullist | link image media table | lineheight outdent indent| forecolor backcolor removeformat | charmap emoticons | code fullscreen preview | save print | pagebreak anchor codesample | ltr rtl',
  skin: 'oxide-dark',
  content_css: 'dark',
  branding: false,
  promotion: false,
  images_upload_handler: async (blobInfo) => {
    try {
      const response = await uploadAnnouncementRichtextImage(blobInfo.blob());
      if (response && response.url) return response.url;
      throw new Error('從伺服器返回的圖片 URL 無效');
    } catch (error) {
      toast.error(error.message || '編輯器圖片上傳失敗');
      throw new Error(`Image upload failed: ${error.message}`);
    }
  },
});

const fetchData = async () => {
  if (!isEditMode.value) {
    announcement.value = {...initialAnnouncement};
    await nextTick();
    showEditor.value = true;
    return;
  }
  isLoading.value = true;
  try {
    const response = await getAnnouncementById(props.announcementId);
    announcement.value = {...initialAnnouncement, ...response};
    if (announcement.value.content === null) announcement.value.content = '';
    await nextTick();
    showEditor.value = true;
  } catch (error) {
    toast.error('無法載入公告資料');
    emit('cancel');
  } finally {
    isLoading.value = false;
  }
};

const triggerFileInput = () => {
  fileInputRef.value.click();
};

const handleCoverImageChange = async (event) => {
  const file = event.target.files[0];
  if (!file) return;
  isUploading.value = true;
  try {
    const response = await uploadAnnouncementImage(file);
    announcement.value.coverImageUrl = response.url;
    toast.success('封面圖片上傳成功');
  } catch (error) {
    toast.error(error.response?.data?.message || '圖片上傳失敗');
  } finally {
    isUploading.value = false;
    event.target.value = '';
  }
};

const submitForm = async () => {
  isSaving.value = true;
  try {
    if (isEditMode.value) {
      await updateAnnouncement(props.announcementId, announcement.value);
    } else {
      await createAnnouncement(announcement.value);
    }
    toast.success('儲存成功！');
    emit('saved');
  } catch (error) {
    toast.error(error.response?.data?.message || '儲存失敗');
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.announcement-form-container {
  padding: 20px;
}

.edit-form {
  background-color: rgba(30, 30, 40, 0.7);
  padding: 30px 40px;
  border-radius: 8px;
  border: 1px solid rgba(160, 100, 220, 0.3);
}

.page-breadcrumb {
  font-size: 0.95em;
  color: #a0a0b0;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
}

.page-breadcrumb .separator {
  margin: 0 8px;
  font-size: 0.7em;
}

.loading-spinner {
  text-align: center;
  padding: 40px;
  font-size: 1.1em;
  color: #888e95;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  color: #a0a0b0;
}

.form-input {
  width: 100%;
  padding: 4px 6px;
  background-color: rgba(20, 20, 30, 0.8);
  border: 1px solid rgba(160, 100, 220, 0.4);
  border-radius: 5px;
  color: #e0e0e0;
  height: 38px;
}

textarea.form-input {
  height: auto;
}

select.form-input {
  padding: 0 12px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23a0a0b0' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right .75rem center;
  background-size: 16px 12px;
}

.image-upload-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.image-preview, .image-placeholder {
  width: 240px;
  height: 135px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  flex-shrink: 0;
  border: 1px solid rgba(160, 100, 220, 0.4);
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.image-placeholder {
  font-size: 3em;
}

.upload-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.upload-actions small {
  color: #888e95;
  font-size: 0.85em;
}

.editor-form-group .editor-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 500px;
  background-color: rgba(20, 20, 30, 0.8);
  border-radius: 5px;
  color: #a0a0b0;
}

.editor-form-group .editor-loading i {
  margin-right: 10px;
}

:deep(.tox-tinymce) {
  border: 1px solid rgba(160, 100, 220, 0.4) !important;
  border-radius: 5px !important;
}

.form-group-inline {
  display: flex;
  gap: 20px;
  align-items: flex-end; /* 關鍵：讓所有子元素的底部對齊 */
}
.form-group-inline .form-group {
  flex: 1; /* 讓兩個區塊平均分配寬度 */
  margin-bottom: 0; /* 移除子元素的底部邊距，讓對齊更精準 */
}
.toggle-form-group {
  display: flex;
  align-items: center;
  border: 1px solid rgba(160, 100, 220, 0.4);
  padding: 4px 6px;
  height: 38px;
  border-radius: 5px;
}

.toggle-switch-label {
  margin: 0;
  flex-grow: 1;
  color: #a0a0b0;
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(80, 80, 90, 0.7);
  transition: .3s;
  border: 1px solid rgba(120, 120, 130, 0.5);
}

.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background-color: #e0e0e0;
  transition: .3s;
}

input:checked + .slider {
  background-color: #50fa7b;
  border-color: #3ddc67;
}

input:checked + .slider:before {
  transform: translateX(22px);
  background-color: #1a1a1a;
}

.slider.round {
  border-radius: 28px;
}

.slider.round:before {
  border-radius: 50%;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(160, 100, 220, 0.15);
}

.action-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.action-btn i {
  margin-right: 6px;
}

.action-btn.cancel-btn {
  background-color: rgba(80, 80, 90, 0.7);
  color: #c0c0c0;
}

.action-btn.cancel-btn:hover {
  background-color: rgba(90, 90, 100, 0.8);
}

.action-btn.save-btn {
  background: linear-gradient(145deg, #50fa7b, #3ddc67);
  color: #1a1a1a;
}

.action-btn.save-btn:hover:not(:disabled) {
  background: linear-gradient(145deg, #60fc8b, #4dfd77);
}

.action-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>